import { z } from "zod";

import { UI_LOCALES } from "@/config/locales";
import { PERMISSIONS } from "@/types/auth";

// Error messages are catalog keys (src/i18n/use-field-message.ts).
const emailSchema = z.string().trim().toLowerCase().max(254).pipe(z.email("validation.email"));

// Same rule as the backend's newPasswordSchema: length matters, character types don't.
const newPasswordSchema = z
  .string()
  .min(12, "validation.passwordMin")
  .max(256, "validation.passwordMax");

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, "validation.passwordRequired").max(256),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const forgotPasswordSchema = z.object({ email: emailSchema });
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

/** The form has a confirm field; only `password` goes to the server. */
export const resetPasswordFormSchema = z
  .object({ password: newPasswordSchema, confirmPassword: z.string() })
  .refine((v) => v.password === v.confirmPassword, {
    message: "validation.passwordsDontMatch",
    path: ["confirmPassword"],
  });
export type ResetPasswordFormInput = z.infer<typeof resetPasswordFormSchema>;

export const resetPasswordSchema = z.object({
  token: z.string().min(20).max(200),
  password: newPasswordSchema,
});

/** Accepting an invitation is the reset flow's twin: same form, different token. */
export const acceptInvitationSchema = resetPasswordSchema;

export const verifyEmailSchema = z.object({ token: z.string().min(20).max(200) });

// MFA (membership-and-mfa.md rules 13–21)

/** The code step of sign-in: a 6-digit TOTP code or a recovery code. */
export const mfaCodeFormSchema = z.object({
  code: z.string().trim().min(1, "validation.codeRequired").max(32, "validation.codeRequired"),
});
export type MfaCodeFormInput = z.infer<typeof mfaCodeFormSchema>;

export const mfaVerifySchema = z.object({
  challengeToken: z.string().min(1).max(64),
  code: z.string().trim().min(1).max(32),
});

/** Enrollment confirms with an authenticator code, never a recovery code. */
export const totpCodeFormSchema = z.object({
  code: z
    .string()
    .trim()
    .regex(/^\d{6}$/, "validation.totpCode"),
});
export type TotpCodeFormInput = z.infer<typeof totpCodeFormSchema>;

export const reauthenticateSchema = z.object({
  password: z.string().min(1, "validation.passwordRequired").max(256),
  code: z.string().trim().min(1).max(32).optional(),
});
export type ReauthenticateInput = z.infer<typeof reauthenticateSchema>;

// Backend response shapes (backend docs/08-authentication.md)
export const tokenPairSchema = z.object({
  accessToken: z.string(),
  accessTokenExpiresAt: z.iso.datetime(),
  refreshToken: z.string(),
  refreshTokenExpiresAt: z.iso.datetime(),
});

/** Rule 16: an enrolled user gets a challenge for the code step instead of tokens. */
export const loginResponseSchema = z.object({
  mfaRequired: z.boolean(),
  challengeToken: z.string().nullable(),
  tokens: tokenPairSchema.nullable(),
});

export const mfaEnrollmentSchema = z.object({ secret: z.string(), otpauthUri: z.string() });
export const recoveryCodesSchema = z.object({ recoveryCodes: z.array(z.string()) });

export const sessionUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  organizationId: z.string(),
  /** False until the user clicks the link in the verification email. */
  emailVerified: z.boolean(),
  // Ignore permissions this frontend doesn't know yet instead of failing the whole session
  permissions: z
    .array(z.string())
    .transform((list) =>
      list.filter((p): p is (typeof PERMISSIONS)[number] =>
        (PERMISSIONS as readonly string[]).includes(p),
      ),
    ),
  /** UI language (FND-03). */
  locale: z.enum(UI_LOCALES),
  /** Enrolled in TOTP (FND-08). */
  mfaEnabled: z.boolean(),
  /** A role requires MFA: without mfaEnabled, the app sends the user to MFA setup. */
  mfaRequired: z.boolean(),
});
export type SessionUser = z.infer<typeof sessionUserSchema>;
